import { ReactElement, useMemo } from 'react';

import { PlanFeaturesTypes } from '~globals/types/enums';
import { useAppSelector } from '~hooks/useAppSelector';
import { useTypePartnerCompany } from '~hooks/useTypePartnerCompany';
import { useSubscriptionFeatures } from '~hooks/useSubscriptionFeatures';
import { selectAuth } from '~store/slices/auth';
import { isOnlyCreatorOrder } from '~utils/user';
import { getFeatureAvailableToDate } from '~utils/feature';

import DialogImportOrdersFromCSV from '../../DialogImportOrdersFromCSV';
import DialogImportOrdersFromIntegration from '../../DialogImportOrdersFromIntegration';
import DialogImportFromAssociatedCompany from '../../DialogImportFromAssociatedCompany';

import { AddOrdersActionsImportDialogsProps } from './types';

const AddOrdersActionsImportDialogs = ({
  items,
  onSubmitImportSuccess,
}: AddOrdersActionsImportDialogsProps): ReactElement => {
  const { user } = useAppSelector(selectAuth);
  const { isCarrier } = useTypePartnerCompany();
  const { hasAccessToFeature } = useSubscriptionFeatures();

  const isUserOnlyCreatorOrder = useMemo(
    () => isOnlyCreatorOrder(user),
    [user],
  );

  const hasAccessMgmGiversModules = useMemo(
    () =>
      hasAccessToFeature(
        PlanFeaturesTypes.MgmGiversModule,
        getFeatureAvailableToDate(PlanFeaturesTypes.MgmGiversModule),
      ),
    [hasAccessToFeature],
  );

  const isEnabledFromFileDialog = useMemo(
    () => !items.fromFile.disabled,
    [items.fromFile.disabled],
  );

  const isEnabledFromIntegrationDialog = useMemo(
    () => !items.fromIntegration.disabled && !isUserOnlyCreatorOrder,
    [items.fromIntegration.disabled, isUserOnlyCreatorOrder],
  );

  const isEnabledFromAssociatedCompanyItem = useMemo(() => {
    if (!hasAccessMgmGiversModules) return false;

    return (
      !items?.fromAssociatedCompany?.disabled &&
      isCarrier &&
      !isUserOnlyCreatorOrder
    );
  }, [
    hasAccessMgmGiversModules,
    items?.fromAssociatedCompany?.disabled,
    isCarrier,
    isUserOnlyCreatorOrder,
  ]);

  return (
    <>
      {isEnabledFromFileDialog && (
        <DialogImportOrdersFromCSV
          open={items.fromFile.open}
          onClose={items.fromFile.onClose}
          onUpdateErrors={items.fromFile.onUpdateErrors}
          enabledFetchAssociatedCompanies={
            items.fromFile.enabledFetchAssociatedCompanies ?? false
          }
          onSubmitSuccess={onSubmitImportSuccess}
        />
      )}

      {isEnabledFromIntegrationDialog && (
        <DialogImportOrdersFromIntegration
          open={items.fromIntegration.open}
          onClose={items.fromIntegration.onClose}
          onEmptyIntegrations={items.fromIntegration.onEmptyIntegrations}
          onSubmitSuccess={onSubmitImportSuccess}
        />
      )}

      {items.fromAssociatedCompany && isEnabledFromAssociatedCompanyItem && (
        <DialogImportFromAssociatedCompany
          open={items.fromAssociatedCompany.open}
          onClose={items.fromAssociatedCompany.onClose}
          onSubmitSuccess={onSubmitImportSuccess}
        />
      )}
    </>
  );
};

export default AddOrdersActionsImportDialogs;
